import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import useAuthStore from '../../stores/authStore';
import { USER_ROLE } from '../../types/auth';
import { superAdminNav, providerNav, NavigationConfig } from '../../config/navigation';

interface SidebarProps {
	isOpen: boolean;
	onClose: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuthStore();

	// Get navigation config based on user role
	const getNavigationConfig = (): NavigationConfig => {
		if (!user) return [];

		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return superAdminNav;
			case USER_ROLE.SALON_OWNER:
				return providerNav;
			default:
				return [];
		}
	};

	const navigationConfig = getNavigationConfig();

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const getUserRoleLabel = () => {
		if (!user) return '';
		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return 'Super Admin';
			case USER_ROLE.SALON_OWNER:
				return 'Salon Owner';
			case USER_ROLE.STAFF:
				return 'Staff';
			default:
				return '';
		}
	};

	const handleNavClick = (path: string) => {
		navigate(path);
		onClose(); // Close sidebar on mobile after navigation
	};

	return (
		<>
			{/* Overlay - Mobile Only */}
			{isOpen && (
				<div
					className="md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
					onClick={onClose}
				/>
			)}

			{/* Sidebar */}
			<aside
				className={`
					w-[260px] bg-white dark:bg-gray-800 shadow-[2px_0_10px_rgba(0,0,0,0.05)] dark:shadow-[2px_0_10px_rgba(0,0,0,0.3)]
					flex flex-col flex-shrink-0 transition-all duration-300
					md:relative md:translate-x-0
					fixed top-0 left-0 h-full z-50 md:pt-0
					${isOpen ? 'translate-x-0' : '-translate-x-full'}
				`}
			>
				{/* Sidebar Header */}
				<div className="px-5 py-6 border-b border-gray-200 dark:border-gray-700">
					{/* User name - Mobile only */}
					<div className="md:hidden text-base font-bold text-gray-800 dark:text-gray-100 mb-1">
						{user?.name}
					</div>
					<div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
						{getUserRoleLabel()}
					</div>
				</div>

				{/* Navigation Menu */}
				<nav className="py-5 flex-1 overflow-y-auto">
					{navigationConfig.map((section, sectionIndex) => (
						<div key={sectionIndex} className="mb-4">
							{section.title && (
								<div className="px-5 pt-4 mb-2 mt-2 border-t border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
									{section.title}
								</div>
							)}
							<ul className="list-none m-0 p-0">
								{section.items
									.filter(item => user && item.roles.includes(user.role))
									.map(item => {
										const isActive =
											location.pathname === item.path ||
											location.pathname.startsWith(item.path + '/');

										return (
											<li key={item.path} className="mb-0.5">
												<button
													onClick={() => handleNavClick(item.path)}
													className={`
														w-full flex items-center gap-3 px-5 py-3 text-left cursor-pointer
														border-l-[3px] font-medium transition-all duration-300
														${
															isActive
																? 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-[#667eea] border-l-[#667eea] font-semibold'
																: 'text-gray-600 dark:text-gray-300 border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-[#667eea] hover:border-l-[#667eea]'
														}
													`}
												>
													<item.icon className="w-6 h-6" />
													<span>{item.label}</span>
												</button>
											</li>
										);
									})}
							</ul>
						</div>
					))}
				</nav>

				{/* Logout Button - Always visible at bottom */}
				<div className="py-4 px-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
					<button
						onClick={handleLogout}
						className="w-full flex items-center gap-3 px-4 py-3 text-left font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all cursor-pointer"
					>
						<ArrowRightStartOnRectangleIcon className="w-6 h-6" />
						<span>Logout</span>
					</button>
				</div>
			</aside>
		</>
	);
};

export default Sidebar;
